import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Copy, Trash2, ShoppingCart } from 'lucide-react';
import { priceBasisLabel } from './constants';
import { computeLineItemTotals, formatQtyTotal, formatWeightTotal } from '../../utils/lineItemTotals';

export function QuotationItemsTable({ quotation, formatCurrency, cloneItem, removeItem }) {
  const itemTotals = computeLineItemTotals(quotation?.items || []);
  if (!quotation?.items || quotation.items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg" data-testid="empty-items-state">
        <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No items added yet</p>
        <p className="text-sm">Search parts above to add to the quotation</p>
        <p className="text-xs mt-2 text-blue-600">Supports Kits and parts without costing (price editable)</p>
      </div>
    );
  }

  return (
    <>
      <div className="table-wrapper overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead>Part No</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Costing #</TableHead>
              <TableHead>Qty</TableHead>
              <TableHead>UOM</TableHead>
              <TableHead>Unit Price</TableHead>
              <TableHead>Per</TableHead>
              <TableHead>Lead Time</TableHead>
              <TableHead>Tooling</TableHead>
              <TableHead>Sample</TableHead>
              <TableHead>Line Total</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {quotation.items.map((item) => (
              <TableRow key={item.id} className="table-row">
                <TableCell className="font-mono font-medium">{item.part_internal_no}</TableCell>
                <TableCell className="max-w-[150px] truncate">{item.description}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{item.costing_number || '-'}</TableCell>
                <TableCell className="font-mono">{item.quantity?.toLocaleString()}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {item.uom === 'kgs' ? 'Kg' : 'Pcs'}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono">{formatCurrency(item.unit_price)}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className="text-xs font-mono">
                    /{priceBasisLabel(item)}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">
                  {item.production_lead_time && <div>Prod: {item.production_lead_time}</div>}
                  {item.sample_lead_time && <div>Sample: {item.sample_lead_time}</div>}
                  {!item.production_lead_time && !item.sample_lead_time && '-'}
                </TableCell>
                <TableCell className="font-mono">{item.tooling_cost > 0 ? formatCurrency(item.tooling_cost) : '-'}</TableCell>
                <TableCell className="font-mono">{item.sample_cost > 0 ? formatCurrency(item.sample_cost) : '-'}</TableCell>
                <TableCell className="font-mono font-medium">{formatCurrency(item.line_total)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" size="sm" 
                      onClick={() => cloneItem(item)} 
                      className="text-primary hover:text-primary"
                      title="Clone this row for volume-based pricing"
                      data-testid={`clone-item-${item.id}`}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => removeItem(item.id)} className="text-destructive" data-testid={`remove-item-${item.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow className="bg-muted/60 font-semibold">
              <TableCell colSpan={3}>TOTAL ({itemTotals.count} item{itemTotals.count === 1 ? '' : 's'})</TableCell>
              <TableCell className="font-mono" data-testid="quotation-total-qty">
                {formatQtyTotal(itemTotals)}
                {formatWeightTotal(itemTotals) !== '-' && (
                  <span className="block text-xs font-normal text-muted-foreground" data-testid="quotation-total-weight">
                    {formatWeightTotal(itemTotals)} Kg total wt
                  </span>
                )}
              </TableCell>
              <TableCell />
              <TableCell colSpan={3} />
              <TableCell className="font-mono">{itemTotals.toolingCost ? formatCurrency(itemTotals.toolingCost) : '-'}</TableCell>
              <TableCell className="font-mono">{itemTotals.sampleCost ? formatCurrency(itemTotals.sampleCost) : '-'}</TableCell>
              <TableCell className="font-mono" data-testid="quotation-total-line-amount">{formatCurrency(itemTotals.lineTotal)}</TableCell>
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>
      </div>

      {/* Totals */}
      <div className="mt-6 flex justify-end">
        <div className="w-80 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Subtotal:</span>
            <span className="font-mono">{formatCurrency(quotation.subtotal)}</span>
          </div>
          {quotation.tooling_total > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tooling Total:</span>
              <span className="font-mono">{formatCurrency(quotation.tooling_total)}</span>
            </div>
          )}
          {quotation.sample_total > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Sample Total:</span>
              <span className="font-mono">{formatCurrency(quotation.sample_total)}</span>
            </div>
          )}
          {quotation.handling_charge > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Handling:</span>
              <span className="font-mono">{formatCurrency(quotation.handling_charge)}</span>
            </div>
          )}
          {quotation.tax_amount > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tax ({quotation.tax_percentage}%):</span>
              <span className="font-mono">{formatCurrency(quotation.tax_amount)}</span>
            </div>
          )}
          {quotation.discount_amount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Discount ({quotation.discount_percentage}%):</span>
              <span className="font-mono">-{formatCurrency(quotation.discount_amount)}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t font-bold text-lg">
            <span>Total:</span>
            <span className="font-mono text-primary" data-testid="quotation-total">{formatCurrency(quotation.total_amount)}</span>
          </div>
        </div>
      </div>
    </>
  );
}
